import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Save } from 'lucide-react'
import toast from 'react-hot-toast'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { empMediaLinksService } from '@/services/empMediaLinksService'
import { employeesService } from '@/services/employeesService'
import type { MediaLinksVm } from '@/api/types/empMediaLinks.types'
import { showError, showSuccess } from '@/lib/swal'

export const MediaLinksEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId
  const isEditing = Boolean(id && id !== 'new')
  const listEmpId = useMemo(() => {
    const n = Number(searchParams.get('empId') ?? searchParams.get('EmpId') ?? 0)
    return n > 0 && !Number.isNaN(n) ? n : 0
  }, [searchParams])
  const backToList = useCallback(() => {
    navigate(listEmpId ? `/media-links?empId=${listEmpId}` : '/media-links')
  }, [navigate, listEmpId])

  const [loading, setLoading] = useState(false)
  const [employees, setEmployees] = useState<{ id?: number; empCode?: string; empFirstNameEn?: string; empSecondNameEn?: string; empLastNameEn?: string }[]>([])

  const [form, setForm] = useState({
    empId: 0,
    twitterLink: '',
    linkedinLink: '',
    instagramLink: '',
    facebookLink: '',
    websiteLink: '',
    otherLink: '',
  })

  useEffect(() => {
    if (!branchId) return
    const loadEmployees = async () => {
      try {
        const res = await employeesService.getAllEmployees(branchId, 1, 500)
        const data = (res as any)?.data ?? (res as any)?.Data ?? res
        setEmployees(Array.isArray(data) ? data : [])
      } catch {
        setEmployees([])
      }
    }
    loadEmployees()
  }, [branchId])

  useEffect(() => {
    if (!isEditing && listEmpId) {
      setForm((prev) => (prev.empId === listEmpId ? prev : { ...prev, empId: listEmpId }))
    }
  }, [isEditing, listEmpId])

  useEffect(() => {
    if (!isEditing || !id) return
    const load = async () => {
      try {
        setLoading(true)
        const data = await empMediaLinksService.getById(Number(id))
        const raw = data as any
        setForm({
          empId: raw.empId ?? raw.employeeId ?? 0,
          twitterLink: raw.twitterLink ?? '',
          linkedinLink: raw.linkedInLink ?? raw.linkedinLink ?? '',
          instagramLink: raw.instagramLink ?? '',
          facebookLink: raw.facebookLink ?? '',
          websiteLink: raw.websiteLink ?? '',
          otherLink: raw.otherLink ?? '',
        })
      } catch (e: any) {
        toast.error(e?.response?.data?.message ?? e?.message ?? t('mediaLinks.loadFailed'))
        backToList()
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isEditing, id, backToList, t])

  const employeeOptions = useMemo(
    () =>
      employees.map((e) => {
        const fullName = [e.empFirstNameEn, e.empSecondNameEn, e.empLastNameEn].filter(Boolean).join(' ').trim()
        const label = fullName ? `${e.empCode ?? ''} — ${fullName}`.trim() : e.empCode ?? t('mediaLinks.employeeFallback', { id: e.id ?? 0 })
        return { value: String(e.id ?? 0), label }
      }).filter((o) => o.value !== '0'),
    [employees, t]
  )

  const employeeSelectLocked = isEditing || listEmpId > 0
  const displayEmpCode = useMemo(() => {
    const emp = employees.find((e) => e.id === form.empId)
    return emp?.empCode?.trim() || '—'
  }, [employees, form.empId])

  const handleChange = (patch: Partial<typeof form>) => setForm((prev) => ({ ...prev, ...patch }))

  const handleSave = async () => {
    if (!branchId) {
      toast.error(t('mediaLinks.branchRequired'))
      return
    }
    if (!form.empId || form.empId === 0) {
      toast.error(t('mediaLinks.employeeRequired'))
      return
    }

    const payload: MediaLinksVm = {
      id: isEditing && id ? Number(id) : 0,
      // Backend expects `empMediaLink` as required identifier
      empMediaLink: isEditing && id ? Number(id) : 0,
      empId: form.empId,
      twitterLink: form.twitterLink.trim() || undefined,
      linkedinLink: form.linkedinLink.trim() || undefined,
      instagramLink: form.instagramLink.trim() || undefined,
      facebookLink: form.facebookLink.trim() || undefined,
      websiteLink: form.websiteLink.trim() || undefined,
      // Some backends treat `otherLink` as a single char; keep 1 character.
      otherLink: (form.otherLink.trim().slice(0, 1) || '-'),
      branchId,
    }

    try {
      setLoading(true)
      if (isEditing) {
        await empMediaLinksService.update(payload)
        await showSuccess(t('mediaLinks.updatedTitle'), t('mediaLinks.updatedMessage'))
      } else {
        await empMediaLinksService.save(payload)
        await showSuccess(t('mediaLinks.savedTitle'), t('mediaLinks.savedMessage'))
      }
      backToList()
    } catch (e: any) {
      await showError(t('mediaLinks.saveFailedTitle'), e?.response?.data?.message ?? e?.message ?? t('mediaLinks.saveFailedMessage'))
    } finally {
      setLoading(false)
    }
  }

  const req = t('mediaLinks.requiredMark')

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('mediaLinks.listTitle')} description={t('mediaLinks.formDescriptionGeneric')} />
        <Card>
          <CardContent className="p-6 text-center text-muted-foreground">{t('mediaLinks.selectBranchFirst')}</CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('mediaLinks.listTitle')}
        description={isEditing ? t('mediaLinks.formDescriptionEdit') : t('mediaLinks.formDescriptionAdd')}
      />
      <Card>
        <CardHeader>
          <CardTitle>{t('mediaLinks.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="emp-code-display">{t('mediaLinks.labelEmpCode')}</Label>
              <Input id="emp-code-display" readOnly value={displayEmpCode} className="bg-muted/50" aria-readonly />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelEmployee')}{req}</Label>
              <SearchableSelect
                options={[{ value: '0', label: t('mediaLinks.placeholderSelectEmployee') }, ...employeeOptions]}
                value={form.empId ? String(form.empId) : '0'}
                onValueChange={(v) => {
                  if (employeeSelectLocked) return
                  handleChange({ empId: Number(v) || 0 })
                }}
                placeholder={t('mediaLinks.placeholderSelectEmployee')}
                searchPlaceholder={t('mediaLinks.searchEmployeePlaceholder')}
                emptyMessage={t('mediaLinks.emptyEmployee')}
                disabled={employeeSelectLocked}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelTwitterLink')}</Label>
              <Input value={form.twitterLink} onChange={(e) => handleChange({ twitterLink: e.target.value })} placeholder={t('mediaLinks.placeholderTwitter')} />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelLinkedInLink')}</Label>
              <Input value={form.linkedinLink} onChange={(e) => handleChange({ linkedinLink: e.target.value })} placeholder={t('mediaLinks.placeholderLinkedIn')} />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelFacebookLink')}</Label>
              <Input value={form.facebookLink} onChange={(e) => handleChange({ facebookLink: e.target.value })} placeholder={t('mediaLinks.placeholderFacebook')} />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelInstagramLink')}</Label>
              <Input value={form.instagramLink} onChange={(e) => handleChange({ instagramLink: e.target.value })} placeholder={t('mediaLinks.placeholderInstagram')} />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelWebsiteLink')}</Label>
              <Input value={form.websiteLink} onChange={(e) => handleChange({ websiteLink: e.target.value })} placeholder={t('mediaLinks.placeholderWebsite')} />
            </div>
            <div className="space-y-2">
              <Label>{t('mediaLinks.labelOtherLink')}</Label>
              <Input value={form.otherLink} onChange={(e) => handleChange({ otherLink: e.target.value })} placeholder={t('mediaLinks.placeholderOther')} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button type="button" variant="outline" onClick={() => backToList()}>
              <ArrowLeft className="w-4 h-4 mr-2" /> {t('mediaLinks.return')}
            </Button>
            <Button type="button" onClick={handleSave} disabled={loading}>
              <Save className="w-4 h-4 mr-2" /> {isEditing ? t('common.update') : t('mediaLinks.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
